// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// THE MECHANISM.
//
// The site's headline is a ratio: over 36 years, roughly three quarters of the
// return to broadacre farm capital came from the land appreciating rather than
// from the farming. That is a result, not an explanation. This view is the
// explanation — five balance-sheet quantities put on one baseline so the reader
// can see WHICH of them moved, and a regional check on whether the same thing
// happened everywhere.
//
// Two claims are pre-empted here on purpose, because both are the first thing a
// farmer says when shown the headline:
//
//   "Farms just got bigger."   Area operated is drawn on the same baseline, and
//                              the per-hectare toggle divides the divergence by
//                              it. It survives: land per hectare ×4.5 against
//                              receipts per hectare ×1.5.
//   "Land only ever goes up."  It does not. Capital appreciation was negative
//                              nationally in eight of the 36 years, in two
//                              clusters, and those years are shaded on the chart
//                              rather than mentioned in a footnote.

import { loadCore, loadMeta } from '../data';
import { CRIMSON, FARMING, GRAPHITE, LAND, NEUTRAL, SCOPE_SHORT, ZONE_ORDER } from '../theme';
import {
  axisBottom, axisLeft, brokenPath, chartFrame, chartSvg, el, extent, finite,
  hatchDefs, isolatedPoints, linearScale, logScale, markTip,
} from '../components/svg';
import {
  TIER_WORD, collapse, evidenceLines, isGap, isOutlineOnly, isRankable,
  isTokenSized, tierOpacity, tierPrefix, type Tier,
} from '../uncertainty';
import { esc, fy, money, num, tip } from '../format';
import { glossIcon, glossTerm } from '../components/glossary';
import { closeButton, openOverlay } from '../overlay';
import { currentRoute, navigate, setParams } from '../router';

/* ─────────────────────────── local data shapes ────────────────────────────
 *
 * core.json carries `tier` on every region-year and `weakYears`/`tier` on every
 * region; src/types.ts still describes the earlier `rel: Reliability` shape and
 * is not mine to edit from this file. These narrow local interfaces describe
 * exactly the fields this view reads, and the loaders' results are cast to them
 * once, at the top, so the rest of the file is properly typed.
 */
interface NatRow {
  year: number;
  receipts: number | null;
  landValue: number | null;
  debt: number | null;
  area: number | null;
  population: number | null;
  appreciation: number | null;
}
interface RegRow {
  year: number;
  tier: Tier | null;
  sample: number | null;
  receipts: number | null;
  landValue: number | null;
  debt: number | null;
  area: number | null;
}
interface Reg {
  name: string;
  zone: string;
  state: string | null;
  /** The region's OWN first surveyed year — three NT regions start in 1992. */
  firstYear: number;
  series: RegRow[];
}
interface MCore {
  years: number[];
  settledYears: number[];
  provisionalYear: number | null;
  national: NatRow[];
  regions: Reg[];
}
interface MMeta {
  headline: {
    perFarm: { fromYear: number; toYear: number; land: number; debt: number; receipts: number; area: number; population: number };
    perHectare: { fromYear: number; toYear: number; land: number; debt: number; receipts: number };
    negativeAppreciationYears: number[];
  };
}

/**
 * The evidence tier the national series is drawn at, and why it is not 1.
 *
 * core.json carries no relative standard error and no sample count for the
 * national rows: ABARES publishes the national aggregate without one in this
 * release. `tierOf(value, null, null)` would return 4 — "no basis" — which is
 * plainly wrong for the pooled result of the entire survey, the largest sample
 * in it. But claiming tier 1 would be inventing a precision nobody published.
 *
 * So the national series sits at tier 2: read the number, allow a margin. It is
 * rendered with the tier-2 chroma collapse and opacity like every other tier-2
 * mark on the site, and it is never used for a superlative.
 */
const NATIONAL_TIER: Tier = 2;

/**
 * The provisional year is drawn one tier worse again.
 *
 * ABARES will revise it. Tier 3 buys exactly the right rendering for free:
 * outline-only dots via `isOutlineOnly`, and every readout tilde-prefixed by
 * `tierPrefix` — "~×5.98" rather than "×5.98". It is excluded from every
 * multiple, every trend sentence and the y-axis of nothing (it still has to fit
 * on the chart, so it still sets the domain).
 */
const PROVISIONAL_TIER: Tier = 3;

type Mode = 'farm' | 'ha';
type SKey = 'land' | 'debt' | 'receipts' | 'area' | 'farms';

interface SeriesDef {
  key: SKey;
  /** Short label for the end of the line, where horizontal room is scarce. */
  short: string;
  label: string;
  colour: string;
  dash: string | null;
  /** Whether the series still means anything once divided by area. */
  inHa: boolean;
  meaning: string;
}

const SERIES: SeriesDef[] = [
  {
    key: 'land', short: 'Land', label: 'Value of land and fixed improvements',
    colour: LAND, dash: null, inHa: true,
    meaning: 'What the ground and the sheds under the business are worth.',
  },
  {
    key: 'debt', short: 'Debt', label: 'Farm business debt at 30 June',
    // Ochre, dashed: debt is the asset's shadow. It is not a second "land"
    // series and it is not money the farming earned, so it may not be teal.
    colour: LAND, dash: '7 4', inHa: true,
    meaning: 'What the business owes. Drawn dashed in the land colour because it tracks the collateral, not the trading.',
  },
  {
    key: 'receipts', short: 'Receipts', label: 'Total cash receipts',
    colour: FARMING, dash: null, inHa: true,
    meaning: 'Everything the farm sold. This is the farming, before any cost is taken out.',
  },
  {
    key: 'area', short: 'Area', label: 'Area operated',
    colour: GRAPHITE, dash: null, inHa: false,
    meaning: 'Hectares per farm — the structural answer to "the farms just got bigger".',
  },
  {
    key: 'farms', short: 'Farms', label: 'Broadacre farms in the survey population',
    colour: GRAPHITE, dash: '2 4', inHa: false,
    meaning: 'How many farms the survey estimates there are. Fewer farms, each one larger.',
  },
];

const CH1_W = 900;
const CH1_H = 470;

export async function renderMechanism(host: HTMLElement): Promise<void> {
  const [coreRaw, metaRaw] = await Promise.all([loadCore(), loadMeta()]);
  const core = coreRaw as unknown as MCore;
  const meta = metaRaw as unknown as MMeta;

  host.textContent = '';

  const nat = core.national.slice().sort((a, b) => a.year - b.year);
  const baseYear = nat[0]?.year ?? core.years[0];
  const settled = core.settledYears;
  const lastSettled = settled[settled.length - 1];
  const prov = core.provisionalYear;
  const byYear = new Map(nat.map((r) => [r.year, r]));

  /** The mode lives in the URL, so a shared link opens on the same reading. */
  let mode: Mode = currentRoute().params.get('per') === 'ha' ? 'ha' : 'farm';
  /** Which series is isolated, or null for "show all". */
  let focus: SKey | null = null;

  const visible = () => SERIES.filter((s) => (mode === 'ha' ? s.inHa : true));

  // ── level, index and multiple ────────────────────────────────────────────
  function level(r: NatRow | undefined, key: SKey, m: Mode): number | null {
    if (!r) return null;
    const per = m === 'ha' ? r.area : 1;
    if (per == null || !(per > 0)) return null;
    switch (key) {
      case 'land': return r.landValue == null ? null : r.landValue / per;
      case 'debt': return r.debt == null ? null : r.debt / per;
      case 'receipts': return r.receipts == null ? null : r.receipts / per;
      case 'area': return m === 'ha' ? null : r.area;
      case 'farms': return m === 'ha' ? null : r.population;
    }
    return null;
  }

  function indexAt(year: number, key: SKey, m: Mode): number | null {
    const v = level(byYear.get(year), key, m);
    const b = level(byYear.get(baseYear), key, m);
    if (v == null || b == null || !(b > 0)) return null;
    return (v / b) * 100;
  }

  /**
   * The multiple, measured on SETTLED years only.
   *
   * Reproduces meta.headline.perFarm / perHectare exactly (land ×5.59 per farm,
   * ×4.51 per hectare) — computed here rather than read from meta so the chart
   * and the number beside it can never drift apart.
   */
  function multiple(key: SKey, m: Mode): number | null {
    const a = level(byYear.get(baseYear), key, m);
    const b = level(byYear.get(lastSettled), key, m);
    if (a == null || b == null || !(a > 0)) return null;
    return b / a;
  }

  function levelText(year: number, key: SKey, m: Mode): string {
    const v = level(byYear.get(year), key, m);
    if (v == null) return '—';
    if (key === 'farms') return `${num(v)} farms`;
    if (key === 'area') return `${num(v)} ha`;
    return m === 'ha' ? `${money(v)}/ha` : money(v);
  }

  const multText = (v: number | null, t: Tier = NATIONAL_TIER) =>
    v == null ? '—' : `${tierPrefix(t)}×${v.toFixed(2)}`;

  // ── the runs of years in which the land LOST value nationally ────────────
  // Read off the data rather than the published list, so the shading cannot go
  // stale against a revised release.
  const negYears = nat.filter((r) => (r.appreciation ?? 0) < 0).map((r) => r.year);
  const negRuns: Array<[number, number]> = [];
  for (const y of negYears) {
    const last = negRuns[negRuns.length - 1];
    if (last && y === last[1] + 1) last[1] = y;
    else negRuns.push([y, y]);
  }
  const negSet = new Set(negYears);

  // ═════════════════════════════ page head ═════════════════════════════════
  const head = document.createElement('div');
  head.className = 'view-head';
  head.innerHTML = `
    <h1>How the gap opened</h1>
    <p class="lede">Across ${settled.length} settled years, the value of the ground under a surveyed
      ${glossTerm('broadacre', 'broadacre')} farm multiplied about five and a half times. What the
      same farm sold multiplied less than twice. Nothing else on this site explains the headline —
      this does.</p>
    <p>Every quantity below is an average <strong>per surveyed farm</strong> ${glossIcon('perFarm')},
      in ${glossTerm('nominal', 'nominal dollars')} ${glossIcon('nominal')}, from the year ending
      June ${baseYear} to the year ending June ${lastSettled}. Years are
      ${glossTerm('financialYear', 'financial years')}: ${fy(lastSettled)} means the twelve months to
      30 June ${lastSettled}.</p>`;
  host.append(head);

  // ═════════════════════════ controls + stat row ═══════════════════════════
  const controls = document.createElement('div');
  controls.className = 'controls';
  controls.innerHTML = `
    <div class="control">
      <span class="field-label" id="mech-mode-label">Measured</span>
      <div class="seg" role="group" aria-labelledby="mech-mode-label">
        <button type="button" data-mode="farm">Per farm</button>
        <button type="button" data-mode="ha">Per hectare</button>
      </div>
    </div>`;
  host.append(controls);

  const modeButtons = [...controls.querySelectorAll<HTMLButtonElement>('[data-mode]')];
  for (const b of modeButtons) {
    b.addEventListener('click', () => {
      const next = b.dataset.mode === 'ha' ? 'ha' : 'farm';
      if (next === mode) return;
      mode = next;
      // Focusing a series that this mode does not draw would leave a pressed
      // control pointing at nothing.
      if (focus && !visible().some((s) => s.key === focus)) focus = null;
      setParams({ per: mode === 'ha' ? 'ha' : null });
      paint();
    });
  }

  const statRow = document.createElement('div');
  statRow.className = 'stat-row';
  host.append(statRow);

  const modeNote = document.createElement('p');
  modeNote.className = 'note small';
  host.append(modeNote);

  // ═════════════════════════════ chart one ═════════════════════════════════
  const card1 = document.createElement('div');
  card1.className = 'card stack';
  card1.innerHTML = `
    <div>
      <h2 class="card-title">Five quantities, one baseline</h2>
      <p class="card-sub">Each line is one balance-sheet quantity, rescaled so its value in
        ${fy(baseYear)} equals 100. A line at 300 means that quantity is three times its
        ${fy(baseYear)} size. Putting them on a shared baseline is the only way to compare a
        dollar figure against a hectare figure against a headcount — the shapes are comparable,
        the units never were.</p>
    </div>`;
  const { frame: frame1, scroll: scroll1 } = chartFrame();
  card1.append(frame1);
  const legend1 = document.createElement('div');
  legend1.className = 'legend';
  card1.append(legend1);
  const chart1Note = document.createElement('p');
  chart1Note.className = 'xsmall muted';
  card1.append(chart1Note);
  host.append(card1);

  // ═════════════════════════════ chart two ═════════════════════════════════
  const card2 = document.createElement('div');
  card2.className = 'card stack';
  card2.style.marginTop = 'var(--space-lg)';
  host.append(card2);

  // ═════════════════════════ closing notes + scope ═════════════════════════
  const tail = document.createElement('div');
  tail.className = 'stack';
  tail.style.marginTop = 'var(--space-lg)';
  host.append(tail);

  // ───────────────────────────── painting ──────────────────────────────────
  function paint(): void {
    for (const b of modeButtons) b.setAttribute('aria-pressed', String((b.dataset.mode ?? 'farm') === mode));
    paintStats();
    paintNote();
    scroll1.textContent = '';
    scroll1.append(buildTrajectory());
    paintLegend();
    applyFocus();
  }

  function paintNote(): void {
    const landHa = multiple('land', 'ha');
    const recHa = multiple('receipts', 'ha');
    modeNote.innerHTML = mode === 'ha'
      ? `<strong>Dividing by size does not close the gap.</strong> Per hectare, land is worth
         ${multText(landHa)} what it was in ${fy(baseYear)} while receipts are ${multText(recHa)}.
         Area and farm numbers are the denominator in this reading, so they are not drawn.`
      : `<strong>Per farm.</strong> Area operated and the number of farms are drawn too, because
         "the farms just got bigger" is the first thing this chart has to answer. Switch to per
         hectare to divide the divergence by size — it survives at
         ${multText(landHa)} against ${multText(recHa)}.`;
  }

  function paintStats(): void {
    statRow.textContent = '';
    for (const s of visible()) {
      const m = multiple(s.key, mode);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'stat';
      btn.style.textAlign = 'left';
      btn.style.cursor = 'pointer';
      btn.style.width = '100%';
      btn.dataset.series = s.key;
      btn.setAttribute('aria-pressed', String(focus === s.key));
      const cls = s.colour === FARMING ? ' farming' : s.colour === LAND ? ' land' : '';
      const headline = s.key === 'farms' && m != null && m < 1
        ? `−${Math.round((1 - m) * 100)}%`
        : multText(m);
      btn.innerHTML = `
        <div class="stat-value${cls}">${esc(headline)}</div>
        <div class="stat-label">${esc(s.short)}${mode === 'ha' && s.inHa ? ' per hectare' : ''}</div>
        <div class="stat-note">${esc(levelText(baseYear, s.key, mode))} → ${esc(levelText(lastSettled, s.key, mode))}</div>`;
      btn.setAttribute('data-tip', tip(
        `${s.label}: ${multText(m)} between ${fy(baseYear)} and ${fy(lastSettled)}`,
        `${levelText(baseYear, s.key, mode)} → ${levelText(lastSettled, s.key, mode)}`,
        s.meaning,
        ...evidenceLines(null, NATIONAL_TIER),
        focus === s.key ? 'Click to show every line again' : 'Click to isolate this line on the chart',
      ));
      btn.addEventListener('click', () => {
        focus = focus === s.key ? null : s.key;
        paintStats();
        paintLegend();
        applyFocus();
      });
      statRow.append(btn);
    }
  }

  function paintLegend(): void {
    legend1.textContent = '';
    for (const s of visible()) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'legend-item';
      b.dataset.series = s.key;
      b.setAttribute('aria-pressed', String(focus === null || focus === s.key));
      const sw = document.createElement('span');
      sw.className = 'legend-swatch';
      // The swatch reproduces the line, dash and all — otherwise two ochre
      // series in the legend read as one.
      sw.style.width = '22px';
      sw.style.height = '0';
      sw.style.borderRadius = '0';
      sw.style.borderTop = `3px ${s.dash ? 'dashed' : 'solid'} ${collapse(s.colour, NATIONAL_TIER)}`;
      const label = document.createElement('span');
      label.textContent = s.label;
      b.append(sw, label);
      b.setAttribute('data-tip', tip(s.meaning, focus === s.key ? 'Click to show every line again' : 'Click to isolate this line'));
      b.addEventListener('click', () => {
        focus = focus === s.key ? null : s.key;
        paintStats();
        paintLegend();
        applyFocus();
      });
      legend1.append(b);
    }
    const note = document.createElement('span');
    note.className = 'legend-item muted';
    note.textContent = prov != null
      ? `Dashed and hatched at the right: ${fy(prov)} is provisional and is in no figure quoted here.`
      : '';
    if (prov != null) legend1.append(note);

    chart1Note.textContent = negRuns.length
      ? `Shaded columns: the ${negYears.length} years in which capital appreciation was negative nationally — `
        + `${negRuns.map(([a, b2]) => (a === b2 ? fy(a) : `${fy(a)} to ${fy(b2)}`)).join(' and ')}. `
        + 'The land line is not a ratchet.'
      : '';
  }

  function applyFocus(): void {
    for (const g of scroll1.querySelectorAll<SVGGElement>('g[data-series]')) {
      const on = focus === null || g.dataset.series === focus;
      g.style.opacity = on ? '1' : '0.12';
    }
  }

  // ───────────────────── chart one: the trajectory ─────────────────────────
  function buildTrajectory(): SVGSVGElement {
    const ml = 56; const mr = 128; const mt = 20; const mb = 54;
    const svg = chartSvg(CH1_W, CH1_H,
      `Five per-${mode === 'ha' ? 'hectare' : 'farm'} quantities from ${fy(baseYear)} to ${fy(core.years[core.years.length - 1])}, each rescaled to 100 in ${fy(baseYear)}`);
    // A hard minimum width plus the .chart-scroll wrapper: the chart scrolls
    // inside its own box on a phone instead of shrinking to illegibility or
    // giving the whole page a sideways scroll.
    svg.style.minWidth = '660px';

    const hatchId = 'mech-prov-hatch';
    svg.append(hatchDefs(hatchId, GRAPHITE, 0.5));

    const years = core.years;
    const x = linearScale([years[0], years[years.length - 1]], [ml, CH1_W - mr]);

    let hi = 100;
    for (const s of visible()) {
      for (const y of years) {
        const v = indexAt(y, s.key, mode);
        if (v != null && v > hi) hi = v;
      }
    }
    const y = linearScale([0, hi * 1.06], [CH1_H - mb, mt]);
    const step = (x(years[1]) - x(years[0])) || 12;

    // ── the years the land fell, shaded behind everything ──────────────────
    // CRIMSON, at low opacity: on this site crimson means below zero and
    // nothing else, and these are exactly the below-zero years.
    for (const [a, b] of negRuns) {
      const x0 = finite(x(a) - step / 2);
      const x1 = finite(x(b) + step / 2);
      svg.append(el('rect', {
        x: x0, y: mt, width: Math.max(2, x1 - x0), height: CH1_H - mb - mt,
        fill: CRIMSON, opacity: 0.07,
      }));
      const lab = el('text', {
        x: (x0 + x1) / 2, y: mt + 12, 'text-anchor': 'middle',
        'font-size': 9.5, fill: CRIMSON, opacity: 0.85,
      });
      lab.textContent = 'land fell';
      svg.append(lab);
    }

    // ── the provisional year, hatched ──────────────────────────────────────
    if (prov != null) {
      const x0 = finite(x(prov) - step / 2);
      svg.append(el('rect', {
        x: x0, y: mt, width: Math.max(4, CH1_W - mr - x0), height: CH1_H - mb - mt,
        fill: `url(#${hatchId})`, opacity: 0.5,
      }));
      const lab = el('text', {
        x: x0 - 4, y: mt + 12, 'text-anchor': 'end', 'font-size': 9.5, fill: GRAPHITE, opacity: 0.8,
      });
      lab.textContent = 'provisional →';
      svg.append(lab);
    }

    axisLeft(svg, y, ml, { gridTo: CH1_W - mr, title: `Index — ${fy(baseYear)} = 100`, format: (v) => String(Math.round(v)) });

    // The baseline itself, so "100" is a line and not just a tick.
    svg.append(el('line', {
      x1: ml, x2: CH1_W - mr, y1: finite(y(100)), y2: finite(y(100)),
      stroke: GRAPHITE, 'stroke-width': 1.2, 'stroke-dasharray': '3 3', opacity: 0.7,
    }));

    // ── one hit column per year, under the dots ────────────────────────────
    // The accessible, keyboard-reachable target is the COLUMN, not the 180
    // individual dots: 180 tab stops in one chart is not an affordance, it is a
    // trap. The column's tooltip reads out every visible series for that year.
    for (const yr of years) {
      const cx = finite(x(yr));
      const rect = el('rect', {
        x: cx - step / 2, y: mt, width: step, height: CH1_H - mb - mt,
        fill: 'transparent', 'pointer-events': 'all', tabindex: 0, role: 'button',
      });
      const t: Tier = prov != null && yr === prov ? PROVISIONAL_TIER : NATIONAL_TIER;
      const row = byYear.get(yr);
      markTip(rect, [
        `${fy(yr)}${prov != null && yr === prov ? ' — provisional' : ''}`,
        ...visible().map((s) => {
          const idx = indexAt(yr, s.key, mode);
          return `${s.short}: ${idx == null ? '—' : `${tierPrefix(t)}${Math.round(idx)}`} (${levelText(yr, s.key, mode)})`;
        }),
        row?.appreciation != null
          ? `Capital appreciation ${money(row.appreciation)}${negSet.has(yr) ? ' — the land lost value this year' : ''}`
          : null,
        ...evidenceLines(null, t),
        'Click for the full year',
      ]);
      const open = () => openYear(yr);
      rect.addEventListener('click', open);
      rect.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); }
      });
      rect.addEventListener('focus', () => rect.setAttribute('fill', 'rgba(62,66,71,0.07)'));
      rect.addEventListener('blur', () => rect.setAttribute('fill', 'transparent'));
      svg.append(rect);
    }

    // ── the series ─────────────────────────────────────────────────────────
    const endLabels: Array<{ yPix: number; text: string; colour: string; g: SVGGElement }> = [];

    for (const s of visible()) {
      const g = el('g', { class: 'mark' });
      g.dataset.series = s.key;
      const stroke = collapse(s.colour, NATIONAL_TIER);

      // brokenPath, not a plain polyline: if a future release drops a year, the
      // line must BREAK there rather than draw a straight decade-long lie.
      const settledPts = settled.map((yr) => {
        const v = indexAt(yr, s.key, mode);
        return { x: finite(x(yr)), y: v == null ? null : finite(y(v)) };
      });
      for (const d of brokenPath(settledPts)) {
        g.append(el('path', {
          d, fill: 'none', stroke, 'stroke-width': 2.2, opacity: tierOpacity(NATIONAL_TIER),
          'stroke-dasharray': s.dash, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
        }));
      }
      // A single surviving year between two gaps would otherwise be invisible.
      for (const p of isolatedPoints(settledPts)) {
        g.append(el('circle', { cx: p.x, cy: p.y, r: 2.6, fill: stroke, opacity: tierOpacity(NATIONAL_TIER) }));
      }

      // The provisional link is drawn separately and dashed, so the settled run
      // never inherits a stroke style that says "revisable".
      if (prov != null) {
        const a = indexAt(lastSettled, s.key, mode);
        const b = indexAt(prov, s.key, mode);
        if (a != null && b != null) {
          g.append(el('line', {
            x1: finite(x(lastSettled)), y1: finite(y(a)), x2: finite(x(prov)), y2: finite(y(b)),
            stroke: collapse(s.colour, PROVISIONAL_TIER), 'stroke-width': 2,
            'stroke-dasharray': '4 4', opacity: tierOpacity(PROVISIONAL_TIER),
          }));
        }
      }

      for (const yr of years) {
        const v = indexAt(yr, s.key, mode);
        if (v == null) continue;
        const t: Tier = prov != null && yr === prov ? PROVISIONAL_TIER : NATIONAL_TIER;
        const outline = isOutlineOnly(t);
        const dot = el('circle', {
          cx: finite(x(yr)), cy: finite(y(v)), r: outline ? 3.4 : 2.6,
          // Tier 3 is outline-only: position and direction, not magnitude.
          fill: outline ? 'none' : collapse(s.colour, t),
          stroke: outline ? collapse(s.colour, t) : 'transparent',
          // A transparent stroke is still a paint value, so it enlarges the hit
          // area to ~7px without drawing anything.
          'stroke-width': outline ? 1.6 : 9,
          opacity: tierOpacity(t),
          'pointer-events': 'all',
        });
        markTip(dot, [
          `${s.label}, ${fy(yr)}: ${tierPrefix(t)}${Math.round(v)} on the ${fy(baseYear)} = 100 index`,
          levelText(yr, s.key, mode),
          ...evidenceLines(null, t),
        ]);
        g.append(dot);
      }

      const endIdx = indexAt(lastSettled, s.key, mode);
      if (endIdx != null) {
        endLabels.push({
          yPix: finite(y(endIdx)),
          text: `${s.short} ${multText(multiple(s.key, mode))}`,
          colour: stroke,
          g,
        });
      }
      svg.append(g);
    }

    // Push overlapping end labels apart rather than letting them collide.
    endLabels.sort((a, b) => a.yPix - b.yPix);
    let prevY = -Infinity;
    for (const lab of endLabels) {
      const yy = Math.max(lab.yPix, prevY + 13);
      prevY = yy;
      const t = el('text', {
        x: CH1_W - mr + 10, y: yy + 4, 'font-size': 11.5, 'font-weight': 600, fill: lab.colour,
      });
      t.textContent = lab.text;
      lab.g.append(t);
    }

    axisBottom(svg, x, CH1_H - mb, {
      ticks: years.filter((v) => v % 5 === 0 || v === years[0] || v === years[years.length - 1]),
      format: (v) => fy(v),
      title: 'Financial year ending 30 June',
    });

    return svg;
  }

  // ───────────────────── the single-year overlay ───────────────────────────
  function openYear(year: number): void {
    const row = byYear.get(year);
    const t: Tier = prov != null && year === prov ? PROVISIONAL_TIER : NATIONAL_TIER;
    openOverlay({
      panelClass: 'modal',
      label: `The surveyed broadacre balance sheet in ${fy(year)}`,
      lockScroll: true,
      build(panel, close) {
        const h = document.createElement('div');
        h.className = 'overlay-head';
        const title = document.createElement('h2');
        title.textContent = `${fy(year)}${prov != null && year === prov ? ' — provisional' : ''}`;
        h.append(title, closeButton(close, `Close ${fy(year)}`));

        const body = document.createElement('div');
        body.className = 'stack small';
        const rows = SERIES.map((s) => {
          const idx = indexAt(year, s.key, mode);
          return `<tr>
            <td>${esc(s.label)}</td>
            <td class="num">${esc(levelText(year, s.key, mode))}</td>
            <td class="num">${idx == null ? '—' : `${tierPrefix(t)}${Math.round(idx)}`}</td>
          </tr>`;
        }).join('');
        const appr = row?.appreciation ?? null;
        body.innerHTML = `
          <p>Per surveyed farm, ${mode === 'ha' ? 'per hectare of the area operated' : 'as an average across the survey'}.
            The index column is that quantity against its ${fy(baseYear)} value.</p>
          <div class="table-scroll">
            <table>
              <thead><tr><th>Quantity</th><th class="num">${mode === 'ha' ? 'Per hectare' : 'Per farm'}</th><th class="num">Index</th></tr></thead>
              <tbody>${rows}</tbody>
            </table>
          </div>
          <p><strong>${glossTerm('capitalAppreciation', 'Capital appreciation')}</strong> that year:
            <span class="mono" style="color:${appr != null && appr < 0 ? CRIMSON : 'inherit'}">${esc(money(appr, { long: true }))}</span>.
            ${appr != null && appr < 0 ? 'The land lost value — a negative year for the half of the return this site is about.' : ''}</p>
          <p class="muted">${esc(TIER_WORD[t])} — ${esc(evidenceLines(null, t).join(' '))}
            ${prov != null && year === prov ? 'This year is provisional: it is excluded from every multiple, mean and trend on this site.' : ''}</p>`;
        panel.append(h, body);
      },
    });
  }

  // ═══════════════ chart two: does it hold region by region? ═══════════════
  interface RegRowCalc {
    name: string; zone: string; state: string | null; firstYear: number;
    land: number | null; receipts: number | null; debt: number | null;
    tier: Tier; baseSample: number | null; endSample: number | null;
    base: RegRow | undefined; end: RegRow | undefined;
  }

  const calc: RegRowCalc[] = core.regions.map((r) => {
    // RULE: every region is rebased on its OWN first surveyed year. Three NT
    // regions enter the survey in 1992; indexing them from 1990 would silently
    // credit them with two years of movement that was never observed.
    const base = r.series.find((s) => s.year === r.firstYear);
    const end = r.series.find((s) => s.year === lastSettled);
    const ratio = (a: number | null | undefined, b: number | null | undefined) =>
      a != null && b != null && a > 0 && b > 0 ? b / a : null;
    // The worse of the two endpoint tiers governs the row: a comparison is only
    // as good as its weaker end. core.json carries no per-variable error for
    // land value or receipts, so the region-year tier — which folds in the
    // sample size and the rate estimate's error — is used as a conservative
    // floor. It can only understate the evidence, never overstate it.
    const tier = Math.max(base?.tier ?? 5, end?.tier ?? 5) as Tier;
    return {
      name: r.name, zone: r.zone, state: r.state, firstYear: r.firstYear,
      land: ratio(base?.landValue, end?.landValue),
      receipts: ratio(base?.receipts, end?.receipts),
      debt: ratio(base?.debt, end?.debt),
      tier, baseSample: base?.sample ?? null, endSample: end?.sample ?? null,
      base, end,
    };
  });

  const placeable = calc.filter((c) => c.land != null && c.receipts != null && !isGap(c.tier) && !isTokenSized(c.tier));
  const tokens = calc.filter((c) => !placeable.includes(c));
  const rankable = placeable.filter((c) => isRankable(c.tier));
  const landAhead = rankable.filter((c) => (c.land ?? 0) > (c.receipts ?? 0)).length;
  const ntLate = calc.filter((c) => c.firstYear !== baseYear);

  card2.innerHTML = `
    <div>
      <h2 class="card-title">Did it happen everywhere?</h2>
      <p class="card-sub">One row per region. The teal dot is what that region's cash receipts
        multiplied by since its own first surveyed year; the ochre dot is what its land value
        multiplied by over exactly the same years. The bar between them is the gap. The scale is
        logarithmic, so equal distances are equal multiples — ×2 to ×4 takes the same room as ×4 to
        ×8 — because on a straight scale a handful of pastoral regions would flatten everything
        else against the left edge.</p>
    </div>`;

  const gate = document.createElement('p');
  gate.className = 'note small';
  gate.innerHTML = `<strong>What may be said, and what may not.</strong>
    ${rankable.length} of the ${calc.length} regions carry firm or fair evidence at BOTH ends of
    this comparison. In ${landAhead === rankable.length ? 'every one of those ' + rankable.length : String(landAhead) + ' of those ' + rankable.length}
    regions the land multiplied by more than the receipts did. The other
    ${calc.length - rankable.length} regions are excluded from that sentence — their rows are drawn
    where the evidence allows, but no count, ordering or superlative on this page uses them.
    ${ntLate.length ? `${ntLate.length} regions (${ntLate.map((c) => esc(c.name.split(' - ')[0])).join(', ')}) enter the survey in ${ntLate[0].firstYear}, so each is measured from its own first year rather than from ${baseYear}.` : ''}`;
  card2.append(gate);

  const { frame: frame2, scroll: scroll2 } = chartFrame();
  card2.append(frame2);
  scroll2.append(buildRegions());

  const legend2 = document.createElement('div');
  legend2.className = 'legend';
  legend2.innerHTML = `
    <span class="legend-item"><span class="legend-swatch" style="background:${collapse(FARMING, 1)}"></span>Cash receipts — what the farming sold</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${collapse(LAND, 1)}"></span>Land and fixed improvements — what the ground is worth</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${collapse(FARMING, 3)};border:1.5px solid ${collapse(FARMING, 3)};background:transparent"></span>Hollow: weak evidence — read the direction, not the number</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${NEUTRAL};border-radius:50%"></span>Fixed-size token: no basis for a figure at all</span>`;
  card2.append(legend2);

  if (tokens.length) {
    const strip = document.createElement('div');
    strip.className = 'stack small';
    strip.style.border = '1px dashed var(--border-default)';
    strip.style.borderRadius = 'var(--radius-md)';
    strip.style.padding = 'var(--space-md)';
    const cap = document.createElement('p');
    cap.className = 'small muted';
    cap.innerHTML = `<strong>${tokens.length} regions cannot be placed on that chart.</strong>
      At one or both endpoints the survey cannot support a figure — too few farms, or an error too
      large to mean anything. Their names are shown; their size is deliberately not. Every token is
      the same size, because encoding a magnitude we do not have is the lie this whole site is
      built to avoid. ${glossTerm('sampleContributing', 'What counts as too few?')}`;
    strip.append(cap);
    const list = document.createElement('div');
    list.style.display = 'flex';
    list.style.flexWrap = 'wrap';
    list.style.gap = 'var(--space-sm)';
    for (const c of tokens) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pill pill-clear';
      b.style.display = 'inline-flex';
      b.style.alignItems = 'center';
      b.style.gap = '6px';
      b.style.maxWidth = '100%';
      b.style.minWidth = '0';
      const dot = document.createElement('span');
      // isTokenSized: fixed radius, neutral fill, magnitude never encoded.
      dot.style.width = '8px';
      dot.style.height = '8px';
      dot.style.borderRadius = '50%';
      dot.style.background = NEUTRAL;
      dot.style.flexShrink = '0';
      const label = document.createElement('span');
      label.className = 'truncate';
      label.textContent = c.name;
      b.append(dot, label);
      b.setAttribute('data-tip', tip(
        `${c.name} — no figure can be placed`,
        `Measured from ${fy(c.firstYear)} to ${fy(lastSettled)}`,
        ...evidenceLines(c.endSample, c.tier),
        'Click for what is known',
      ));
      b.addEventListener('click', () => openRegion(c));
      list.append(b);
    }
    strip.append(list);
    card2.append(strip);
  }

  function buildRegions(): SVGSVGElement {
    const W = 900; const ml = 210; const mr = 54; const mt = 16; const mb = 56;
    const rowH = 24; const zoneGap = 26;

    // Grouped by zone, and within a zone ordered by the land multiple. The
    // ordering is a reading aid, not a claim: the only sentence this page makes
    // about "which region" is gated to firm and fair evidence above.
    const groups = ZONE_ORDER
      .map((z) => ({ zone: z, rows: placeable.filter((c) => c.zone === z).sort((a, b) => (b.land ?? 0) - (a.land ?? 0)) }))
      .filter((g) => g.rows.length);

    let cursor = mt;
    const layout: Array<{ row: RegRowCalc; y: number }> = [];
    const headers: Array<{ zone: string; y: number; n: number }> = [];
    for (const g of groups) {
      cursor += zoneGap;
      headers.push({ zone: g.zone, y: cursor - 8, n: g.rows.length });
      for (const r of g.rows) { layout.push({ row: r, y: cursor + rowH / 2 }); cursor += rowH; }
      cursor += 6;
    }
    const H = cursor + mb;

    const svg = chartSvg(W, H, `Land value multiple against cash receipts multiple for ${placeable.length} regions, grouped by agro-climatic zone`);
    svg.style.minWidth = '700px';

    const all = placeable.flatMap((c) => [c.land, c.receipts]);
    const [lo, hi] = extent(all);
    // logScale REFUSES non-positive input rather than clamping it, so a region
    // whose receipts fell to zero would be dropped, never drawn on the floor.
    // Every multiple here is a ratio of two positive dollar figures, so all of
    // them are strictly positive — but the guard stays.
    const x = logScale([Math.max(0.4, lo * 0.85), hi * 1.15], [ml, W - mr]);
    const ticks = x.ticks();

    for (const t of ticks) {
      const px = x(t);
      if (!Number.isFinite(px)) continue;
      svg.append(el('line', {
        x1: px, x2: px, y1: mt, y2: cursor,
        class: Math.abs(t - 1) < 1e-9 ? 'zero-line' : 'grid-line',
      }));
    }

    for (const h of headers) {
      const t = el('text', { x: 8, y: h.y, 'font-size': 11.5, 'font-weight': 700, fill: GRAPHITE });
      t.textContent = `${h.zone} zone — ${h.n} region${h.n === 1 ? '' : 's'}`;
      svg.append(t);
    }

    for (const { row, y: ry } of layout) {
      const xr = x(row.receipts ?? 1);
      const xl = x(row.land ?? 1);
      if (!Number.isFinite(xr) || !Number.isFinite(xl)) continue;
      const outline = isOutlineOnly(row.tier);

      const name = el('text', { x: ml - 12, y: ry + 4, 'text-anchor': 'end', class: 'axis-text' });
      name.textContent = clip(row.name, 30);
      svg.append(name);

      svg.append(el('line', {
        x1: finite(xr), x2: finite(xl), y1: ry, y2: ry,
        stroke: GRAPHITE, 'stroke-width': 1.4, opacity: tierOpacity(row.tier) * 0.5,
      }));

      for (const [px, colour] of [[xr, FARMING], [xl, LAND]] as Array<[number, string]>) {
        svg.append(el('circle', {
          cx: finite(px), cy: ry, r: outline ? 5 : 4.6,
          fill: outline ? 'none' : collapse(colour, row.tier),
          stroke: outline ? collapse(colour, row.tier) : 'none',
          'stroke-width': outline ? 1.6 : 0,
          opacity: tierOpacity(row.tier),
        }));
      }

      const hit = el('rect', {
        x: 0, y: ry - rowH / 2, width: W - mr, height: rowH,
        fill: 'transparent', 'pointer-events': 'all', tabindex: 0, role: 'button',
      });
      markTip(hit, [
        `${row.name}: land ${multText(row.land, row.tier)}, receipts ${multText(row.receipts, row.tier)} since ${fy(row.firstYear)}`,
        row.firstYear !== baseYear ? `Measured from ${fy(row.firstYear)} — this region entered the survey late` : null,
        `${row.baseSample ?? '—'} farms surveyed in ${fy(row.firstYear)}`,
        ...evidenceLines(row.endSample, row.tier),
        'Click for the region',
      ]);
      const open = () => openRegion(row);
      hit.addEventListener('click', open);
      hit.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); }
      });
      hit.addEventListener('focus', () => hit.setAttribute('fill', 'rgba(62,66,71,0.07)'));
      hit.addEventListener('blur', () => hit.setAttribute('fill', 'transparent'));
      svg.append(hit);
    }

    axisBottom(svg, x, cursor + 8, {
      ticks, format: (v) => `×${v < 1 ? v.toFixed(1) : v}`, x0: ml, x1: W - mr,
      title: `Multiple of the region's own first surveyed year, by ${fy(lastSettled)}`,
    });

    return svg;
  }

  function openRegion(c: RegRowCalc): void {
    openOverlay({
      panelClass: 'modal',
      label: `${c.name}: land against receipts`,
      lockScroll: true,
      build(panel, close) {
        const h = document.createElement('div');
        h.className = 'overlay-head';
        const title = document.createElement('h2');
        title.textContent = c.name;
        h.append(title, closeButton(close, `Close ${c.name}`));

        const line = (label: string, a: number | null | undefined, b: number | null | undefined, fmt: (v: number | null | undefined) => string, mult: number | null) => `
          <tr>
            <td>${esc(label)}</td>
            <td class="num">${esc(fmt(a))}</td>
            <td class="num">${esc(fmt(b))}</td>
            <td class="num">${esc(multText(mult, c.tier))}</td>
          </tr>`;

        const body = document.createElement('div');
        body.className = 'stack small';
        body.innerHTML = `
          <p class="muted">${esc(c.zone)} zone${c.state ? ` · ${esc(c.state)}` : ''} · first surveyed ${esc(fy(c.firstYear))}
            ${c.firstYear !== baseYear ? '<strong>— later than the rest of the survey, so every multiple here is measured from that year, not from ' + esc(fy(baseYear)) + '.</strong>' : ''}</p>
          <div class="table-scroll">
            <table>
              <thead><tr><th>Per farm</th><th class="num">${esc(fy(c.firstYear))}</th><th class="num">${esc(fy(lastSettled))}</th><th class="num">Multiple</th></tr></thead>
              <tbody>
                ${line('Land and fixed improvements', c.base?.landValue, c.end?.landValue, (v) => money(v), c.land)}
                ${line('Total cash receipts', c.base?.receipts, c.end?.receipts, (v) => money(v), c.receipts)}
                ${line('Farm business debt', c.base?.debt, c.end?.debt, (v) => money(v), c.debt)}
                ${line('Area operated', c.base?.area, c.end?.area, (v) => (v == null ? '—' : `${num(v)} ha`), c.base?.area && c.end?.area ? c.end.area / c.base.area : null)}
              </tbody>
            </table>
          </div>
          <p><strong>Evidence: ${esc(TIER_WORD[c.tier])}.</strong>
            ${esc(c.baseSample == null ? 'Sample not published' : `${c.baseSample} farms surveyed`)} in ${esc(fy(c.firstYear))},
            ${esc(c.endSample == null ? 'sample not published' : `${c.endSample} in ${fy(lastSettled)}`)}.
            A comparison is only as good as its weaker end, so the worse of the two years governs this row.
            ${isRankable(c.tier) ? 'This region is inside every counted statement on this page.' : 'This region is excluded from every count, ordering and superlative on this page.'}</p>
          <p class="muted">${esc(glossaryFreeNote(c))}</p>`;
        panel.append(h, body);
      },
    });
  }

  function glossaryFreeNote(c: RegRowCalc): string {
    if (isTokenSized(c.tier)) {
      return 'Because the survey cannot support a figure at one or both endpoints, this region is shown as a fixed-size token rather than plotted. The dollar figures above are still exactly what ABARES published — what is missing is any basis for treating the difference between them as measured.';
    }
    if (isOutlineOnly(c.tier)) {
      return 'Drawn hollow on the chart: read the direction of the gap, not its width.';
    }
    return 'Drawn solid on the chart: the gap between the two dots is as measured as anything in this survey gets.';
  }

  // ────────────────────────── closing notes ────────────────────────────────
  const perFarm = meta.headline.perFarm;
  const caveat = document.createElement('div');
  caveat.className = 'note';
  caveat.innerHTML = `
    <p><strong>What this does not show.</strong> An index says how far something moved, never how big
      it was — a line at 400 on a small number is still a small number. And none of these lines is a
      return: the land multiplying by ${perFarm.land.toFixed(2)} is a paper gain
      ${glossIcon('capitalAppreciation')} that a farm realises only by selling, while debt
      multiplying by ${perFarm.debt.toFixed(2)} is a bill that arrives every quarter. The
      ${glossTerm('rateOfReturn', 'rate of return')} pages put both back into the same measure.</p>`;
  tail.append(caveat);

  const method = document.createElement('p');
  method.className = 'small muted';
  method.innerHTML = `<strong>Method.</strong> Every figure is ABARES' published per-farm estimate,
    divided by another published per-farm estimate at most — nothing here is re-derived from a ratio
    ABARES also publishes. ${prov != null ? `${fy(prov)} is provisional: it is drawn hatched and dashed and appears in no multiple, mean or trend above.` : ''}
    The national rows carry no published sampling error in this release, so they are drawn at the
    "fair" tier — read the number, allow a margin — and are never used for a superlative. Regional
    rows use the region-year tier, which folds together the sample size and the published error, as
    a conservative floor for land value and receipts.`;
  tail.append(method);

  const jump = document.createElement('div');
  jump.className = 'row';
  const toLedger = document.createElement('button');
  toLedger.type = 'button';
  toLedger.className = 'btn btn-sm';
  toLedger.textContent = 'See what the two engines added up to →';
  toLedger.setAttribute('data-tip', 'Open the ledger: the same 36 years as money, farming against land');
  toLedger.addEventListener('click', () => navigate('ledger'));
  jump.append(toLedger);
  tail.append(jump);

  const scope = document.createElement('p');
  scope.className = 'xsmall muted';
  scope.textContent = SCOPE_SHORT;
  tail.append(scope);

  paint();
}

/** Plain-text truncation for SVG <text>, which has no ellipsis of its own. */
function clip(s: string, max: number): string {
  if (s.length <= max) return s;
  const cut = s.slice(0, max - 1);
  const sp = cut.lastIndexOf(' ');
  return `${sp > max * 0.55 ? cut.slice(0, sp) : cut}…`;
}
